import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { AppState } from '../app.state';
import { ControlEventsService } from '../../services/control-events.service';
import { catchError, debounceTime, filter, map, of, switchMap } from 'rxjs';
import { ControlEventsActions } from '@states/control-events/control-events.action-types';
import { withLatestFrom } from 'rxjs/operators';
import { ControlEventsSelectors } from '@states/control-events/control-events.selector-types';
import * as SharedActions from '@states/shared/shared.actions';
import { TranslateService } from '@ngx-translate/core';
import { SharedApiService } from '../../services/shared-api.service';
import { GrantedAccessSelectors } from '@states/granted-access/granted-access.selector-types';

@Injectable()
export class ControlEventsEffect {
  public getControlEventsList$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ControlEventsActions.getControlEvents),
      withLatestFrom(
        this.store$.select(ControlEventsSelectors.selectQueryParams),
        this.store$.select(ControlEventsSelectors.selectPagination),
      ),
      switchMap(([_, queryParams, pagination]) => {
        return this.controlEventsService.getControlEvents(pagination.page, pagination.size, queryParams).pipe(
          map((result) => ControlEventsActions.getControlEventsSuccess({ result: result })),
          catchError((response) => {
            return [ControlEventsActions.getControlEventsFail(), this.catchError(response)];
          }),
        );
      }),
    ),
  );

  public loadNextControlEventsPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ControlEventsActions.loadNextPage),
      withLatestFrom(
        this.store$.select(ControlEventsSelectors.selectCurrentCount),
        this.store$.select(ControlEventsSelectors.selectTotal),
        this.store$.select(ControlEventsSelectors.selectQueryParams),
        this.store$.select(ControlEventsSelectors.selectPagination),
      ),
      filter(([action, current, total, queryParams, pagination]) => {
        return current < total - 1;
      }),
      switchMap(([_, __, ___, queryParams, pagination]) => {
        return this.controlEventsService.getControlEvents(pagination.page, pagination.size, queryParams).pipe(
          map((result) => ControlEventsActions.getControlEventsSuccess({ result })),
          catchError((error) => of(ControlEventsActions.getControlEventsFail())),
        );
      }),
    ),
  );

  public getDistinct$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ControlEventsActions.getDistinct),
      switchMap(() => {
        return this.controlEventsService.getDistinct().pipe(
          map((distinct) => ControlEventsActions.getDistinctSuccess({ distinct })),
          catchError((response) => {
            return [ControlEventsActions.getDistinctFail(), this.catchError(response)];
          }),
        );
      }),
    ),
  );

  public onQueryParamsChanged$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ControlEventsActions.updateQueryParamsFilter),
      debounceTime(400),
      switchMap(({ filters }) => {
        return [ControlEventsActions.getControlEvents()];
      }),
    ),
  );

  public getControlEventDetails = createEffect(() =>
    this.actions$.pipe(
      ofType(ControlEventsActions.getEventDetails),
      withLatestFrom(this.store$.select(GrantedAccessSelectors.selectAccessToken)),
      switchMap(([{ id }, shareAccessToken]) => {
        const obs = shareAccessToken
          ? this.sharedApiService.getAccessControlEventDetailsShared(id)
          : this.controlEventsService.getControlEventDetails(id);
        return obs.pipe(
          map((eventDetails) => ControlEventsActions.getEventDetailsSuccess({ eventDetails })),
          catchError((response) => {
            return [ControlEventsActions.getEventDetailsFail(), this.catchError(response)];
          }),
        );
      }),
    ),
  );

  public getControlEventComments = createEffect(() =>
    this.actions$.pipe(
      ofType(ControlEventsActions.getEventsComments),
      switchMap(({ eventId }) => {
        return this.controlEventsService.getControlEventMessages(eventId).pipe(
          map((comments) => ControlEventsActions.getEventsCommentsSuccess({ comments })),
          catchError((response) => {
            return [ControlEventsActions.getEventsCommentsFail(), this.catchError(response)];
          }),
        );
      }),
    ),
  );

  public addControlEventComment = createEffect(() =>
    this.actions$.pipe(
      ofType(ControlEventsActions.addEventComment),
      switchMap(({ eventId, comment }) => {
        return this.controlEventsService.createControlEventComment(eventId, comment).pipe(
          switchMap((status) => {
            return [
              SharedActions.showMessage({ success: this.translateService.instant('TOASTS.CONTROL_EVENT_MSG_ADDED_SUCCESS') }),
              ControlEventsActions.addEventCommentSuccess(),
              ControlEventsActions.getEventsComments({ eventId }),
            ];
          }),
          catchError((error) => of(ControlEventsActions.addEventCommentFail())),
        );
      }),
    ),
  );

  public updateControlEventComment = createEffect(() =>
    this.actions$.pipe(
      ofType(ControlEventsActions.updateEventComment),
      switchMap(({ eventId, comment }) => {
        return this.controlEventsService.updateControlEventComment(comment).pipe(
          switchMap((status) => {
            return [
              SharedActions.showMessage({ success: this.translateService.instant('TOASTS.CONTROL_EVENT_MSG_UPDATED_SUCCESS') }),
              ControlEventsActions.updateEventCommentSuccess(),
              ControlEventsActions.getEventsComments({ eventId }),
            ];
          }),
          catchError((error) => of(ControlEventsActions.addEventCommentFail())),
        );
      }),
    ),
  );

  public deleteControlEventComment = createEffect(() =>
    this.actions$.pipe(
      ofType(ControlEventsActions.deleteEventComment),
      switchMap(({ eventId, commentId }) => {
        return this.controlEventsService.deleteControlEventComment(commentId).pipe(
          switchMap((status) => {
            return [
              SharedActions.showMessage({ success: this.translateService.instant('TOASTS.CONTROL_EVENT_MSG_DELETED_SUCCESS') }),
              ControlEventsActions.deleteEventCommentSuccess(),
              ControlEventsActions.getEventsComments({ eventId }),
            ];
          }),
          catchError((error) => of(ControlEventsActions.addEventCommentFail())),
        );
      }),
    ),
  );

  private catchError(response) {
    return SharedActions.showMessage({ error: response?.error?.message });
  }

  constructor(
    private actions$: Actions,
    private store$: Store<AppState>,
    private controlEventsService: ControlEventsService,
    private translateService: TranslateService,
    private sharedApiService: SharedApiService,
  ) {}
}
